import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Chip,
  Typography,
} from "@mui/material";

import { useEffect, useState, useMemo } from "react";

import { useBszFinderContext } from "../../context/bszContext";
import KsbDialogActions from "../../../../2_ksbHub/components/KsbDialogActions";

export default function BszClassesDialog({ open, onClose }) {
  const { classes = [], myClasses = [], saveMyClasses } = useBszFinderContext();
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  /**
   * =========================================================
   * ✅ INIT
   * =========================================================
   */
  useEffect(() => {
    if (open) {
      setSelected(myClasses.map((c) => c.id));
    }
  }, [open, myClasses]);

  /**
   * =========================================================
   * ✅ GROUP CLASSES WITH "WEITERE"
   * =========================================================
   */
  const groupedClasses = useMemo(() => {
    const map = {};
    const other = [];

    classes.forEach((c) => {
      const name = c.name;

      // ✅ match pattern: G25a, U26b
      const isStandard = /^[A-Z]\d{2}[a-z]$/i.test(name);

      if (!isStandard) {
        other.push(c);
        return;
      }

      const group = name.slice(0, -1);

      if (!map[group]) map[group] = [];
      map[group].push(c);
    });

    // ✅ add "Weitere"
    if (other.length > 0) {
      map["Weitere"] = other;
    }

    return map;
  }, [classes]);

  /**
   * =========================================================
   * ✅ TOGGLE
   * =========================================================
   */
  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  /**
   * =========================================================
   * ✅ SAVE
   * =========================================================
   */
  const handleSave = async () => {
    try {
      setSaving(true);
      await saveMyClasses(selected);
      onClose();
    } catch (err) {
      console.error("❌ saving classes failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const hasClasses = classes.length > 0;

  /**
   * =========================================================
   * ✅ RENDER
   * =========================================================
   */
  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        Welche Klassen gehören zu dir?
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            opacity: 0.7,
            fontStyle: "italic",
          }}
        >
          Wähle alle Klassen aus, die du unterrichtest oder besuchst 😄
        </Typography>
      </DialogTitle>

      <DialogContent>
        {!hasClasses ? (
          <Typography sx={{ opacity: 0.6 }}>Keine Klassen verfügbar</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Object.entries(groupedClasses)
              .sort(([a], [b]) => {
                // ✅ keep "Weitere" last
                if (a === "Weitere") return 1;
                if (b === "Weitere") return -1;
                return a.localeCompare(b, "de");
              })
              .map(([group, items]) => (
                <Box key={group}>
                  {/* ✅ GROUP LABEL */}
                  <Typography variant="caption" sx={{ opacity: 0.6, ml: 0.5 }}>
                    {group}
                  </Typography>

                  {/* ✅ CHIPS */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    {items
                      .sort((a, b) => a.name.localeCompare(b.name, "de"))
                      .map((c) => (
                        <Chip
                          key={c.id}
                          label={c.name}
                          clickable
                          size="small"
                          color={
                            selected.includes(c.id) ? "primary" : "default"
                          }
                          onClick={() => toggle(c.id)}
                        />
                      ))}
                  </Box>
                </Box>
              ))}
          </Box>
        )}
      </DialogContent>

      <KsbDialogActions
        onCancel={onClose}
        onConfirm={handleSave}
        confirmLabel="Speichern"
        confirmDisabled={!hasClasses || selected.length === 0 || saving}
        confirmLoading={saving}
      />
    </Dialog>
  );
}
